import { FetchResult } from '@apollo/client/link/core'
import isEqual from 'lodash/isEqual'

import { CartAddressInput, CartFragment, Maybe, ShippingAddressInput } from '../../graphql/magento'
import { checkAndDiscardQueryResponse } from '../../utils/graphqlTools'
import {
  areAddressFieldValuesEqual,
  fieldValuesFromCartAddress,
  shouldSaveInAddressBook,
} from './address'
import { shouldUpdateBillingAddress } from './billing'
import { CartUpdateError } from './errors'
import { CustomerAddressFromQuery, ValidFormValues } from './types'

export const isPractitionerShippingDetails = (
  values: ValidFormValues.ShippingDetails,
): values is ValidFormValues.PractitionerShippingDetails => 'dropShip' in values

export const shouldUpdateShippingAddress = ({
  cart,
  addressValues,
}: {
  cart: CartFragment
  addressValues: ValidFormValues.Address
}): boolean => {
  const [cartShippingAddress] = cart.shipping_addresses
  return (
    !cartShippingAddress ||
    !areAddressFieldValuesEqual(fieldValuesFromCartAddress(cartShippingAddress), addressValues)
  )
}

type CartAddressSelection = NonNullable<
  CartFragment['billing_address'] | CartFragment['shipping_addresses'][0]
>
const areCartAddressesEqual = (a: CartAddressSelection, b: CartAddressSelection): boolean =>
  a.city === b.city &&
  a.country.code === b.country.code &&
  a.country.label === b.country.label &&
  a.firstname === b.firstname &&
  a.lastname === b.lastname &&
  a.postcode === b.postcode &&
  a.region?.code === b.region?.code &&
  a.region?.label === b.region?.label &&
  a.street.join() === b.street.join() &&
  a.telephone === b.telephone

export const isBillingSameAsShipping = (cart: CartFragment): boolean | undefined => {
  const {
    billing_address: cartBillingAddress,
    shipping_addresses: [cartShippingAddress],
  } = cart
  return !!cartBillingAddress && !!cartShippingAddress
    ? areCartAddressesEqual(cartBillingAddress, cartShippingAddress)
    : undefined
}

export const isBillingUndefinedOrSameAsShipping = (cart: CartFragment): boolean => {
  const {
    billing_address: cartBillingAddress,
    shipping_addresses: [cartShippingAddress],
  } = cart
  const pickForCompare = (
    address: NonNullable<typeof cartBillingAddress> | NonNullable<typeof cartShippingAddress>,
  ) => ({
    city: address.city,
    country: address.country,
    firstname: address.firstname,
    lastname: address.lastname,
    postcode: address.postcode,
    region: address.region,
    street: address.street,
    telephone: address.telephone,
  })
  // if either is undefined, update billing to match shipping
  if (!cartBillingAddress || !cartShippingAddress) {
    return true
  }
  return isEqual(pickForCompare(cartBillingAddress), pickForCompare(cartShippingAddress))
}

export const shouldUpdateDropShip = ({
  cart,
  dropShipValue,
}: {
  cart: CartFragment
  dropShipValue?: string
}): boolean => dropShipValue != null && cart.drop_ship !== dropShipValue

export const updateBillingWithShippingAddressIfNeeded =
  ({
    addressInput,
    addressValues,
    customerCart,
    updateBillingAddress,
  }: {
    addressInput: CartAddressInput
    addressValues: ValidFormValues.Address
    customerCart: CartFragment
    updateBillingAddress: (
      address: NonNullable<ShippingAddressInput['address']>,
    ) => Promise<FetchResult>
  }) =>
  (): Promise<void> =>
    // update billing address if undefined or same as shipping and differs from
    // field values
    isBillingUndefinedOrSameAsShipping(customerCart) &&
    shouldUpdateBillingAddress({ cart: customerCart, addressValues })
      ? updateBillingAddress({ ...addressInput, save_in_address_book: false })
          .then(checkAndDiscardQueryResponse)
          .catch((err) => {
            throw new CartUpdateError('updateBillingAddress', err)
          })
      : Promise.resolve()

export const updateShippingAddressIfNeeded =
  ({
    addressInput,
    addressValues,
    afterUpdate,
    customerAddresses,
    customerCart,
    updateShippingAddress,
  }: {
    addressInput: CartAddressInput
    addressValues: ValidFormValues.Address
    afterUpdate?: () => Promise<void>
    customerAddresses?: Maybe<CustomerAddressFromQuery[]>
    customerCart: CartFragment
    updateShippingAddress: (
      address: NonNullable<ShippingAddressInput['address']>,
    ) => Promise<FetchResult>
  }) =>
  (): Promise<void> =>
    shouldUpdateShippingAddress({ cart: customerCart, addressValues })
      ? updateShippingAddress({
          ...addressInput,
          save_in_address_book: shouldSaveInAddressBook({
            customerAddresses,
            fieldValues: addressValues,
          }),
        })
          .then(checkAndDiscardQueryResponse)
          .catch((err) => {
            throw new CartUpdateError('updateShippingAddress', err)
          })
          .then(() => (afterUpdate ? afterUpdate() : Promise.resolve()))
      : Promise.resolve()

export const updateDropShipIfNeeded =
  ({
    customerCart,
    dropShipValue,
    updateDropShip,
  }: {
    customerCart: CartFragment
    dropShipValue: string
    updateDropShip: (value: string) => Promise<FetchResult>
  }) =>
  (): Promise<void> =>
    shouldUpdateDropShip({ cart: customerCart, dropShipValue })
      ? updateDropShip(dropShipValue)
          .then(checkAndDiscardQueryResponse)
          .catch((err) => {
            throw new CartUpdateError('updateDropShip', err)
          })
      : Promise.resolve()
